/**
 * ════════════════════════════════════════════════════════════════════════
 * Keep a section anchor id on the copy that is actually visible.
 *
 * Two sections can legitimately carry the SAME `anchor_id` when one is
 * desktop-only and the other mobile-only — the bundle page does exactly this,
 * with one card grid per breakpoint behind a single "Shop bundles" link.
 *
 * Both always render: snippets/section-visibility.liquid hides by CSS, not by
 * omitting markup. So the document holds two elements with the same id, and
 * fragment navigation resolves to the FIRST in document order. When that is
 * the hidden one, the link silently does nothing — which is why such a link
 * appears to work on one breakpoint only.
 *
 * This moves the canonical id onto whichever copy is on screen and suffixes
 * the rest, re-running when the viewport crosses a breakpoint. Sections with a
 * unique anchor are left alone, so the cost is a no-op for every normal page.
 * ════════════════════════════════════════════════════════════════════════
 */

/** Marks an id parked on a hidden duplicate; never linked to directly. */
const PARKED = '--anchor-hidden';

/** @param {Element} el */
function isVisible(el) {
  // offsetParent is null for display:none (and for position:fixed, hence the
  // rect fallback — a fixed section would still be on screen).
  return /** @type {HTMLElement} */ (el).offsetParent !== null || el.getClientRects().length > 0;
}

function resolveAnchors() {
  /** @type {Map<string, Element[]>} */
  const groups = new Map();

  for (const el of document.querySelectorAll('[data-anchor-id]')) {
    const key = /** @type {HTMLElement} */ (el).dataset.anchorId;
    if (!key) continue;
    const group = groups.get(key);
    if (group) group.push(el);
    else groups.set(key, [el]);
  }

  for (const [id, els] of groups) {
    if (els.length === 1) {
      els[0].id = id;
      continue;
    }
    const winner = els.find(isVisible) ?? els[0];
    els.forEach((el, i) => {
      el.id = el === winner ? id : `${id}${PARKED}-${i}`;
    });
  }
}

/**
 * A hash already in the URL was resolved by the browser BEFORE this ran, so it
 * may have scrolled to the wrong copy (or nowhere). Redo it once ids are right.
 */
function rescrollToHash() {
  const id = decodeURIComponent(location.hash.slice(1));
  if (!id) return;
  document.getElementById(id)?.scrollIntoView({ block: 'start' });
}

resolveAnchors();
rescrollToHash();

// Re-resolve when the viewport crosses the breakpoint the visibility CSS uses,
// rather than on every resize frame.
matchMedia('(min-width: 750px)').addEventListener('change', resolveAnchors);
